<script lang="ts" setup>
/**
 * The right-hand drawer, behind roughly 140 call sites.
 *
 * The prop surface and the single `close` emit are frozen: filters, create and
 * edit forms, column pickers, the mobile "More" sheet in `layouts/default.vue`
 * and a long tail of others all bind to them, so this is a rebuild of the
 * inside, not a redesign of the contract.
 *
 * Dismissal is deliberately NOT widened. Today only the close button dismisses
 * it, and most of those 140 call sites hold a half-filled form — click-outside
 * or Escape would be a brand new way to lose somebody's work in a hundred
 * places at once. `closeOnEscape` exists for callers that know their content is
 * safe to abandon, and it defaults to the behaviour that shipped.
 */
import { nextTick, onBeforeUnmount, ref, watch } from "vue";
import { lockBodyScroll } from "~/composable/useBodyScrollLock";

const emit = defineEmits(["close"]);

const props = defineProps({
  hideMask: {
    default: "",
    type: String,
  },
  showEditModal: {
    required: true,
    type: Boolean,
  },
  width: {
    default: "w-[350px] sm:w-[400px]",
    type: String,
  },
  bgColor: {
    default: "bg-white",
    type: String,
  },
  /** Accessible name for the dialog. Announced instead of "dialog". */
  label: {
    default: "Panel",
    type: String,
  },
  /**
   * Opt-in, because the drawer is full of unsaved forms. See the note above.
   */
  closeOnEscape: {
    default: false,
    type: Boolean,
  },
});

const panel = ref<HTMLElement | null>(null);
let previouslyFocused: HTMLElement | null = null;

/*
 * Scroll lock, from `composable/useBodyScrollLock`.
 *
 * It lives there rather than here because the counter has to be shared, and a
 * counter declared in `<script setup>` is not: that block is the setup function
 * body, so every binding in it is per-instance. Two overlapping drawers each
 * kept their own count and their own idea of the original overflow, and the
 * second one to close restored `hidden` — leaving the page permanently
 * unscrollable until a refresh. The full sequence is written out in that file.
 */
let releaseScroll: (() => void) | null = null;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const focusable = () =>
  Array.from(
    panel.value?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
  ).filter((el) => el.offsetParent !== null || el === document.activeElement);

/**
 * Tab stays inside the drawer.
 *
 * Without this, tabbing off the last field walks into the page behind — which
 * is still rendered and still scrollable-to — and the person is editing a form
 * they can no longer see.
 */
const onKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape" && props.closeOnEscape) {
    event.stopPropagation();
    close();
    return;
  }

  if (event.key !== "Tab") return;

  const items = focusable();
  if (!items.length) return;

  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;

  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};

watch(
  () => props.showEditModal,
  async (open) => {
    if (open) {
      previouslyFocused = (document.activeElement as HTMLElement) ?? null;
      releaseScroll?.();
      releaseScroll = lockBodyScroll();

      await nextTick();
      // The panel itself is the fallback target: a drawer whose first control is
      // far down a long form should not scroll itself on open.
      (focusable()[0] ?? panel.value)?.focus?.();
      return;
    }

    releaseScroll?.();
    releaseScroll = null;

    // Back where they were, so closing a drawer does not dump focus at the top
    // of the document.
    previouslyFocused?.focus?.();
    previouslyFocused = null;
  },
);

/*
 * A drawer can be unmounted while open — a route change with the panel up, or a
 * `v-if` on an ancestor. Without this the body keeps `overflow: hidden` and the
 * page is left permanently unscrollable with nothing on screen to explain it.
 */
onBeforeUnmount(() => {
  releaseScroll?.();
  releaseScroll = null;
});

function close() {
  emit("close");
}
</script>

<template>
  <Teleport to="body">
    <ModalMask
      class="edit-modal-mask"
      :class="`${!props.showEditModal ? 'none' : ''} ${props.hideMask}`"
    >
      <Transition name="drawer">
        <div
          v-if="props.showEditModal"
          ref="panel"
          role="dialog"
          aria-modal="true"
          :aria-label="props.label"
          tabindex="-1"
          class="edit-modal-panel h-[100vh] overflow-clip absolute z-[500] right-0 feature-container rounded-none pt-10 pr-0 focus:outline-none"
          :class="`${props.width} ${props.bgColor}`"
          @keydown="onKeydown"
        >
          <!-- Top-right, round and quiet. It was a square hard-cornered button
               pinned to the top-LEFT, i.e. the far side from the scrollbar and
               from where every other product in the world puts it. -->
          <button
            type="button"
            class="edit-modal-close"
            aria-label="Close panel"
            @click="close"
          >
            <Svgs name="close" />
          </button>

          <div
            class="flex flex-col gap-5 max-h-[90vh] overflow-auto w-full px-10 pb-12"
          >
            <slot></slot>
          </div>
        </div>
      </Transition>
    </ModalMask>
  </Teleport>
</template>

<style scoped>
.none {
  display: none !important;
}

/* A touch deeper and less grey than the shared mask's own bg-gray-500/50, so
   the page reads as behind the drawer rather than merely faded. */
.edit-modal-mask {
  background-color: rgba(17, 14, 38, 0.5) !important;
}

/*
 * The panel keeps `feature-container` from the original markup and that is not
 * cosmetic: it is where `dark:bg-dark-blue` comes from. The `bgColor` prop
 * defaults to `bg-white`, so dropping the class turns all ~140 drawers white in
 * dark mode — the class list is part of the contract, like the props.
 *
 * Only the drop shadow is added here, cast leftwards so the panel reads as
 * standing in front of the page rather than pasted onto its edge.
 */
.edit-modal-panel {
  box-shadow: -24px 0 60px -24px rgba(24, 18, 56, 0.35);
}

.dark .edit-modal-panel {
  box-shadow: -24px 0 60px -24px rgba(0, 0, 0, 0.6);
}

.edit-modal-close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 1;
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  color: var(--home-muted-fg, #67608a);
  transition:
    background-color 150ms ease,
    color 150ms ease;
}

.edit-modal-close:hover {
  background-color: rgba(59, 42, 130, 0.08);
  color: var(--light-purple);
}

.dark .edit-modal-close:hover {
  background-color: rgb(255 255 255 / 0.1);
  color: #fff;
}

.edit-modal-close:focus-visible {
  outline: 2px solid var(--light-sec-purple);
  outline-offset: 2px;
}

/*
 * Slides on `transform`, not on `right`.
 *
 * The old keyframe animated `right: -400px → 0`, which is a layout property AND
 * a hard-coded width: every caller passing something wider than the 400px
 * default — and plenty do — started its slide from part-way across the screen
 * rather than from off it. `translateX(100%)` is the panel's own width whatever
 * that turns out to be, and it composites instead of reflowing.
 */
.drawer-enter-active,
.drawer-leave-active {
  transition: transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
}

.drawer-enter-from,
.drawer-leave-to {
  transform: translateX(100%);
}

@media (prefers-reduced-motion: reduce) {
  .drawer-enter-active,
  .drawer-leave-active {
    transition-duration: 1ms;
  }
}
</style>
